/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState } from "react";
import { api, formatApiError, spriteUrl } from "@/lib/api";
import { toast } from "sonner";
import { Lock, X } from "lucide-react";

export default function RecruitModal({ open, onClose, onRecruited }) {
  const [options, setOptions] = useState(null);
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPicked(null);
    api.get("/recruit/options").then(({ data }) => setOptions(data)).catch(() => setOptions(null));
  }, [open]);

  if (!open) return null;

  const canRecruit = options && options.trainers_count < options.max_slots;

  const submit = async () => {
    if (!picked) return;
    setBusy(true);
    try {
      const { data } = await api.post("/trainers/recruit", { starter_pokemon_id: picked });
      toast.success(`${data.name} rejoint le labo !`);
      onRecruited?.();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4 overflow-auto"
      style={{ background: "rgba(26,26,26,0.5)", zIndex: 70 }}
      onClick={onClose}
      data-testid="recruit-modal"
    >
      <div className="panel-strong p-6 max-w-3xl w-full my-8 relative" onClick={(e) => e.stopPropagation()}>
        <button data-testid="recruit-close-btn" className="absolute top-3 right-3 btn-ink p-1 px-1.5" onClick={onClose}>
          <X size={14}/>
        </button>
        <div className="overline">Bureau du Professeur · Recrutement</div>
        <h2 className="font-serif-display text-3xl leading-none mt-1">Engager un nouveau dresseur</h2>
        <p className="text-xs text-[#4A4A4A] mt-2 mb-4">
          Le candidat choisira son premier Pokémon parmi les starters des régions débloquées.
          Sa personnalité sera révélée lors du recrutement.
        </p>

        {!options ? (
          <div className="font-mono text-xs text-[#7A7A7A]">Chargement...</div>
        ) : !canRecruit ? (
          <LockedNotice options={options}/>
        ) : (
          <>
            <div className="mb-3 panel p-2 flex justify-between text-xs font-mono">
              <span>Slots utilisés : <strong>{options.trainers_count}/{options.max_slots}</strong></span>
              {options.max_slots >= 12 && !options.next_slot ? (
                <span className="text-[#2E4F32] font-bold">✓ Équipe complète</span>
              ) : options.next_slot && (
                <span className="text-[#4A4A4A]">
                  Prochain slot : {options.next_slot.label}
                </span>
              )}
            </div>

            <div className="space-y-4 max-h-[60vh] overflow-auto pr-1">
              {options.starters_by_region.map((reg) => (
                <div key={reg.region_id}>
                  <div className="overline mb-2 flex items-center gap-2">
                    Région · {reg.region_name}
                    <span className="h-px bg-[#2B2B2B] flex-1"/>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    {reg.starters.map((s) => (
                      <button
                        key={s.pokemon_id}
                        type="button"
                        onClick={() => setPicked(s.pokemon_id)}
                        data-testid={`starter-pick-${s.pokemon_id}`}
                        className={`panel p-3 flex flex-col items-center text-center transition-all ${picked === s.pokemon_id ? "bg-[#1A1A1A] text-[#F4EFE6] border-[#1A1A1A]" : "hover:bg-[#EAE4D9]"}`}
                      >
                        <img src={spriteUrl(s.pokemon_id)} alt={s.name} className="pixelated w-20 h-20"/>
                        <div className="font-serif-display text-xl leading-tight mt-1">{s.name}</div>
                        <div className={`text-[10px] font-mono uppercase tracking-wider ${picked === s.pokemon_id ? "text-[#F4EFE6]" : "text-[#4A4A4A]"}`}>
                          {s.type} · N°{s.pokemon_id.toString().padStart(3, "0")}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button className="btn-ink" onClick={onClose} data-testid="recruit-cancel-btn">Annuler</button>
              <button
                data-testid="recruit-confirm-btn"
                className="btn-ink btn-leaf"
                disabled={!picked || busy}
                onClick={submit}
              >
                {busy ? "Recrutement..." : "Confirmer le recrutement"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function LockedNotice({ options }) {
  return (
    <div className="panel p-4 flex items-start gap-3" data-testid="recruit-locked">
      <Lock size={20} className="text-[#8C2A2A] shrink-0 mt-1"/>
      <div>
        <div className="font-serif-display text-xl leading-tight">Aucun slot disponible</div>
        <p className="text-xs text-[#4A4A4A] mt-1">
          Vous utilisez {options.trainers_count}/{options.max_slots} slots.
          Pour débloquer le prochain :
        </p>
        {options.next_slot && (
          <ul className="mt-2 text-xs font-mono text-[#1A1A1A] space-y-1">
            {options.next_slot.conditions.map((c, i) => (
              <li key={i} className="border border-[#B87333] text-[#B87333] px-2 py-1 inline-block mr-1">
                {describeCondition(c)}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export function describeCondition(c) {
  switch (c.type) {
    case "pokemon_caught_unique": return `Capturer ${c.value} Pokémon différents`;
    case "badges_total": return `${c.value} badges (cumulés)`;
    case "trainer_badges": return `1 dresseur avec ${c.value} badges`;
    case "league_winners": return `${c.value} Maître(s) de la Ligue`;
    case "region_unlocked": return `Débloquer la région ${c.value}`;
    case "kanto_dex_no_legendary": return "Pokédex Kanto complet (hors légendaires)";
    case "johto_caught": return `${c.value} Pokémon de Johto capturés`;
    case "always": return "Toujours débloqué";
    default: return JSON.stringify(c);
  }
}
